import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelSelect as _vModelSelect, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, vModelText as _vModelText, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, vShow as _vShow, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "w-full px-2 py-2 sm:px-4 md:px-6 lg:px-8" }
const _hoisted_2 = { class: "container mx-auto" }
const _hoisted_3 = { class: "bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-8" }
const _hoisted_4 = { class: "space-y-5" }
const _hoisted_5 = {
  key: 0,
  class: "flex items-center gap-3 -mb-2"
}
const _hoisted_6 = ["onUpdate:modelValue"]
const _hoisted_7 = { class: "flex flex-col sm:flex-row gap-3 group" }
const _hoisted_8 = { class: "relative flex-1" }
const _hoisted_9 = ["onUpdate:modelValue", "onInput", "placeholder"]
const _hoisted_10 = { class: "w-full sm:w-64" }
const _hoisted_11 = { class: "relative" }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = { class: "flex flex-wrap gap-1 flex-1" }
const _hoisted_14 = ["onClick"]
const _hoisted_15 = {
  key: 1,
  class: "bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm flex items-center gap-1"
}
const _hoisted_16 = { class: "absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-xl" }
const _hoisted_17 = { class: "p-3" }
const _hoisted_18 = { class: "flex justify-between items-center mb-3" }
const _hoisted_19 = ["onClick"]
const _hoisted_20 = { class: "relative mb-3" }
const _hoisted_21 = ["onUpdate:modelValue"]
const _hoisted_22 = { class: "max-h-[40vh] sm:max-h-60 overflow-y-auto" }
const _hoisted_23 = ["onClick"]
const _hoisted_24 = {
  key: 0,
  class: "w-3 h-3 text-white",
  fill: "none",
  stroke: "currentColor"
}
const _hoisted_25 = { class: "text-sm text-gray-700" }
const _hoisted_26 = ["onClick"]
const _hoisted_27 = { class: "space-y-4" }
const _hoisted_28 = { class: "flex items-center gap-2 mb-2" }
const _hoisted_29 = {
  key: 0,
  class: "inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm font-semibold"
}
const _hoisted_30 = {
  key: 1,
  class: "inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm font-semibold"
}
const _hoisted_31 = {
  key: 0,
  class: "py-8"
}
const _hoisted_32 = {
  key: 1,
  class: "flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2"
}
const _hoisted_33 = { class: "text-sm text-gray-600" }
const _hoisted_34 = {
  key: 0,
  class: "flex items-center gap-2"
}
const _hoisted_35 = { key: 0 }
const _hoisted_36 = { key: 1 }
const _hoisted_37 = {
  key: 2,
  class: "transition-opacity duration-300 overflow-x-auto"
}
const _hoisted_38 = {
  key: 3,
  class: "mt-4"
}
const _hoisted_39 = {
  key: 4,
  class: "text-center py-8"
}

import LoadingIndicator from "@/components/LoadingIndicator.vue";
import Pagination from "@/components/Pagination.vue";
import QueryTable from "@/components/QueryTable.vue";
import SuggestionDropdown from "@/components/SuggestionDropdown.vue";
import type { Query } from "@/models/Query";
import { mapQueryType, transformQueries } from "@/models/Query";
import rawQueries from "@/queries.json";
import { loadFiltersFromStorage, saveFiltersToStorage, type FilterState } from "@/utils/filterPersistence";
import Fuse from "fuse.js";
import { onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

// Table headers
interface ScopeOption {
  value: string;
  label: string;
}

// Reactive state
const itemsPerPage = 20;
interface SearchCondition {
  id: number;
  logic?: "AND" | "OR" | "NOT";
  text: string;
  scopes: ScopeOption[];
  searchQuery?: string;
}

// In the <script> section - updated scopeOptions:

export default /*@__PURE__*/_defineComponent({
  __name: 'AdvancedSearch',
  setup(__props) {

const tableHeaders = [
  { key: "id", label: "Query ID" },
  { key: "neuronId", label: "Neuron ID" },
  { key: "neuronText", label: "SQL Query", searchable: true },
  { key: "author", label: "Author" },
  { key: "extraction", label: "Run Count" },
  { key: "queryStartTime", label: "Run Date" },
];

const isDropdownOpen = ref(false);
const queries = ref<Query[]>(transformQueries(rawQueries as any[]));
const filteredSearchResults = ref<Query[]>([]);
const displayedResults = ref<Query[]>([]);
const currentPage = ref(1);
const showUniqueQueries = ref(true);
const router = useRouter();

// Sorting state
const sortState = ref<{
  key: string;
  direction: "asc" | "desc";
}>({
  key: "id",
  direction: "asc",
});

// Search condition logic
const scopeOptions: ScopeOption[] = [
  { label: "Query ID", value: "id" },
  { label: "Query Type", value: "componentType" },
  { label: "SQL Query", value: "neuronText" },
  { label: "Authors", value: "author" },
  { label: "Time Period", value: "timePeriod" },
  { label: "Mentioned", value: "mentioned" },
  { label: "Inferences", value: "inferences" },
  { label: "Features", value: "features" },
  { label: "Table Names", value: "tableNames" },
  { label: "Column Names", value: "columnNames" },
  { label: "Medication Enrichments", value: "medicationEnrichments" },
  { label: "Indication Enrichments", value: "indicationEnrichments" },
  { label: "Run Count", value: "extraction" },
];

const searchConditions = ref<SearchCondition[]>([
  {
    id: Date.now(),
    text: "",
    scopes: [],
  },
]);

const dropdownStates = ref<Record<number, boolean>>({});
const searchQueries = ref<Record<number, string>>({});

// Add new reactive state for suggestions
const suggestions = ref<
  {
    id: string;
    text: string;
    type: string;
    author: string;
    runCount: number;
    date: string;
    tags: string[];
    features: string[];
    description: string;
    score: number;
    lastRun: string;
    isRelated: boolean;
    matchType: string;
    matchedScope: string;
    scopeLabel: string;
    highlightedText: string;
  }[]
>([]);
const showSuggestions = ref(false);
const selectedSuggestionIndex = ref(-1);

// Initialize Fuse.js instance for suggestions
const fuseInstance = ref<Fuse<Query> | null>(null);

// Initialize Fuse.js when queries are loaded
watch(
  () => queries.value,
  (newQueries) => {
    initializeFuse(newQueries);
  },
  { immediate: true }
);

// Initialize Fuse.js with the query data
function initializeFuse(queries: Query[]) {
  const fuseOptions = {
    keys: scopeOptions.map((scope) => ({
      name: scope.value,
      weight: getWeightForScope(scope.value),
    })),
    threshold: 0.3,
    includeScore: true,
    ignoreLocation: true,
    distance: 100,
    useExtendedSearch: true,
    findAllMatches: true,
    minMatchCharLength: 2,
    shouldSort: true,
    limit: 8,
  };

  fuseInstance.value = new Fuse(queries, fuseOptions);
}

// Add weight calculation for different scopes
function getWeightForScope(scope: string): number {
  const weights: Record<string, number> = {
    neuronText: 1.0,
    id: 0.9,
    componentType: 0.8,
    author: 0.7,
    features: 0.6,
    tableNames: 0.5,
    columnNames: 0.5,
    inferences: 0.4,
    mentioned: 0.4,
    medicationEnrichments: 0.3,
    indicationEnrichments: 0.3,
    timePeriod: 0.3,
    extraction: 0.2,
  };
  return weights[scope] || 0.3;
}

// Fix the timeout type in the debounce function
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: number | undefined;
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = window.setTimeout(later, wait);
  };
}

// Add new reactive state for input handling
const isGeneratingSuggestions = ref(false);
const lastSearchText = ref("");

// Simple debounced search functions
const debouncedGenerateSuggestions = debounce(
  (text: string, condition: SearchCondition) => {
    generateSuggestions(text, condition);
  },
  300
);

// Update the generateSuggestions function to be more efficient
const generateSuggestions = async (
  text: string,
  condition: SearchCondition
) => {
  if (!text.trim() || !fuseInstance.value || text.length < 2) {
    suggestions.value = [];
    showSuggestions.value = false;
    return;
  }

  // Prevent duplicate searches
  if (text === lastSearchText.value) return;
  lastSearchText.value = text;

  try {
    isGeneratingSuggestions.value = true;

    // Get selected scopes or use all scopes if none selected
    const selectedScopes =
      condition.scopes.length > 0
        ? condition.scopes.map((s) => s.value)
        : scopeOptions.map((s) => s.value);

    // Create a Fuse query that respects scopes
    const fuseQuery = {
      $or: selectedScopes.map((scope) => ({
        [scope]: text,
      })),
    };

    const results = fuseInstance.value.search(fuseQuery);

    // Group results by scope more efficiently
    const scopedResults = new Map<string, typeof results>();

    // Process results in chunks to prevent UI blocking
    const chunkSize = 50;
    for (let i = 0; i < results.length; i += chunkSize) {
      const chunk = results.slice(i, i + chunkSize);
      await new Promise((resolve) => setTimeout(resolve, 0)); // Allow UI updates

      chunk.forEach((result) => {
        const matchedScope = findMatchedScope(
          result.item,
          text,
          selectedScopes
        );
        if (!scopedResults.has(matchedScope)) {
          scopedResults.set(matchedScope, []);
        }
        scopedResults.get(matchedScope)?.push(result);
      });
    }

    // Transform suggestions more efficiently
    suggestions.value = Array.from(scopedResults.entries())
      .slice(0, 5) // Limit number of scope groups
      .flatMap(([scope, scopeResults]) =>
        scopeResults
          .slice(0, 10) // Limit results per scope
          .map((result) => ({
            // Core query data (same as HeroSection)
            id: result.item.id,
            text: result.item.neuronText, // Full SQL text like HeroSection
            type: result.item.componentType,
            author: result.item.author,
            runCount: result.item.extraction || 0,
            date: result.item.queryStartTime,
            tags: result.item.tags || [],
            features: result.item.features || [],
            // Additional suggestion metadata
            description: result.item.description?.main || "",
            score: result.score || 0,
            lastRun: result.item.queryStartTime,
            isRelated: Boolean(result.score && result.score > 0.3),
            matchType:
              result.score && result.score <= 0.3 ? "direct" : "related",
            matchedScope: scope,
            scopeLabel:
              scopeOptions.find((s) => s.value === scope)?.label || scope,
            highlightedText: highlightMatchedText(result.item.neuronText, text),
          }))
      );

    showSuggestions.value = suggestions.value.length > 0;
    selectedSuggestionIndex.value = -1;
  } catch (error) {
    console.error("Error generating suggestions:", error);
    suggestions.value = [];
    showSuggestions.value = false;
  } finally {
    isGeneratingSuggestions.value = false;
  }
};

// Helper function to find which scope matched
function findMatchedScope(
  item: Query,
  searchText: string,
  scopes: string[]
): string {
  const textLower = searchText.toLowerCase();
  for (const scope of scopes) {
    const value = (item as any)[scope];
    if (!value) continue;

    if (Array.isArray(value)) {
      if (value.some((v) => String(v).toLowerCase().includes(textLower))) {
        return scope;
      }
    } else if (String(value).toLowerCase().includes(textLower)) {
      return scope;
    }
  }
  return scopes[0]; // fallback to first scope
}

// Helper function to get display text based on scope
function getDisplayTextForScope(item: Query, scope: string): string {
  const value = (item as any)[scope];
  if (!value) return "";

  if (Array.isArray(value)) {
    return value.join(", ");
  }
  return String(value);
}

// Helper function to highlight matched text
function highlightMatchedText(text: string, searchText: string): string {
  if (!searchText.trim()) return text;
  const regex = new RegExp(`(${searchText})`, "gi");
  return text.replace(
    regex,
    '<mark class="bg-[#157813]/10 text-[#157813] px-1 rounded">$1</mark>'
  );
}

// Add keyboard navigation for suggestions
const handleKeyDown = (event: KeyboardEvent) => {
  if (!showSuggestions.value) return;

  switch (event.key) {
    case "ArrowDown":
      event.preventDefault();
      selectedSuggestionIndex.value = Math.min(
        selectedSuggestionIndex.value + 1,
        suggestions.value.length - 1
      );
      break;
    case "ArrowUp":
      event.preventDefault();
      selectedSuggestionIndex.value = Math.max(
        selectedSuggestionIndex.value - 1,
        -1
      );
      break;
    case "Enter":
      event.preventDefault();
      if (selectedSuggestionIndex.value >= 0) {
        const selectedSuggestion =
          suggestions.value[selectedSuggestionIndex.value];
        searchConditions.value[0].text = selectedSuggestion.text;
        showSuggestions.value = false;
        suggestions.value = [];
        executeSearch();
      }
      break;
    case "Escape":
      event.preventDefault();
      showSuggestions.value = false;
      suggestions.value = [];
      break;
  }
};

// Methods
const addCondition = () => {
  searchConditions.value.push({
    id: Date.now() + Math.random(),
    logic: "AND",
    text: "",
    scopes: [],
  });
};

const removeCondition = (index: number) => {
  searchConditions.value.splice(index, 1);
};

const filteredOptions = (condition: SearchCondition) => {
  return scopeOptions.filter((opt) =>
    opt.label
      .toLowerCase()
      .includes((searchQueries.value[condition.id] || "").toLowerCase())
  );
};

const isSelected = (condition: SearchCondition, option: ScopeOption) => {
  return condition.scopes.some((s) => s.value === option.value);
};

const toggleOption = (condition: SearchCondition, option: ScopeOption) => {
  const index = condition.scopes.findIndex((s) => s.value === option.value);
  if (index === -1) {
    condition.scopes.push(option);
  } else {
    condition.scopes.splice(index, 1);
  }
  // Close dropdown after selection
  dropdownStates.value[condition.id] = false;
};

const toggleDropdown = (conditionId: number) => {
  const condition = searchConditions.value.find((c) => c.id === conditionId);
  if (condition && filteredOptions(condition).length > 0) {
    dropdownStates.value[conditionId] = !dropdownStates.value[conditionId];
  }
};

// Sorting functionality
const sortBy = (key: string) => {
  if (sortState.value.key === key) {
    sortState.value.direction =
      sortState.value.direction === "asc" ? "desc" : "asc";
  } else {
    sortState.value.key = key;
    sortState.value.direction = "asc";
  }
  executeSearch();
};

// Add new reactive state for loading and animations
const isSearching = ref(false);
const hasSearched = ref(false);
const isInitialized = ref(false);

// Update executeSearch function with loading state and animations
const executeSearch = async () => {
  try {
    isSearching.value = true;
    hasSearched.value = true;

    // Execute search logic immediately without delay
    if (!searchConditions.value.length) {
      filteredSearchResults.value = queries.value;
    } else {
      let currentSet = new Set<string>();
      searchConditions.value.forEach((cond, idx) => {
        const newSet = findMatches(cond);

        if (idx === 0) {
          currentSet = newSet;
        } else {
          switch (cond.logic) {
            case "AND":
              currentSet = intersect(currentSet, newSet);
              break;
            case "OR":
              currentSet = union(currentSet, newSet);
              break;
            case "NOT":
              currentSet = difference(currentSet, newSet);
              break;
          }
        }
      });

      filteredSearchResults.value = queries.value.filter((q) =>
        currentSet.has(q.id)
      );
    }

    // Apply unique filter if enabled
    if (showUniqueQueries.value) {
      const uniqueMap = new Map();
      filteredSearchResults.value.forEach((query) => {
        if (!uniqueMap.has(query.neuronId)) {
          uniqueMap.set(query.neuronId, query);
        }
      });
      filteredSearchResults.value = Array.from(uniqueMap.values());
    }

    // Apply sorting with improved ID handling
    filteredSearchResults.value.sort((a, b) => {
      const aValue = a[sortState.value.key as keyof Query];
      const bValue = b[sortState.value.key as keyof Query];

      if (sortState.value.key === "id") {
        // Sort by ID as string, but handle numeric parts properly
        const aStr = String(aValue || "");
        const bStr = String(bValue || "");
        return sortState.value.direction === "asc"
          ? aStr.localeCompare(bStr, undefined, { numeric: true })
          : bStr.localeCompare(aStr, undefined, { numeric: true });
      }

      if (sortState.value.key === "neuronId") {
        // Extract numbers after AST_ for comparison
        const numA = parseInt(String(aValue).split("AST_")[1] || "0", 10);
        const numB = parseInt(String(bValue).split("AST_")[1] || "0", 10);
        return sortState.value.direction === "asc" ? numA - numB : numB - numA;
      }

      if (sortState.value.key === "queryStartTime") {
        // Handle undefined/null values for dates
        if (!aValue) return sortState.value.direction === "asc" ? -1 : 1;
        if (!bValue) return sortState.value.direction === "asc" ? 1 : -1;

        // Ensure we're working with string dates
        const aString = typeof aValue === "string" ? aValue : "";
        const bString = typeof bValue === "string" ? bValue : "";

        const aDate = new Date(aString).getTime();
        const bDate = new Date(bString).getTime();

        // Handle invalid dates
        if (isNaN(aDate) && isNaN(bDate)) return 0;
        if (isNaN(aDate)) return sortState.value.direction === "asc" ? -1 : 1;
        if (isNaN(bDate)) return sortState.value.direction === "asc" ? 1 : -1;

        return sortState.value.direction === "asc"
          ? aDate - bDate
          : bDate - aDate;
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortState.value.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      // Default string comparison
      return sortState.value.direction === "asc"
        ? String(aValue || "").localeCompare(String(bValue || ""))
        : String(bValue || "").localeCompare(String(aValue || ""));
    });

    // Reset pagination and update displayed results
    currentPage.value = 1;
    updateDisplayedResults();
  } catch (error) {
    console.error("Error in executeSearch:", error);
    // Fallback: show all queries if search fails
    filteredSearchResults.value = queries.value;
    currentPage.value = 1;
    updateDisplayedResults();
  } finally {
    // Always clear loading state immediately
    isSearching.value = false;
  }
};

// Handle page change
const handlePageChange = (page: number) => {
  currentPage.value = page;
  updateDisplayedResults();
};

// Update displayed results based on current page
const updateDisplayedResults = () => {
  const startIndex = (currentPage.value - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  displayedResults.value = filteredSearchResults.value.slice(startIndex, endIndex);
};

// CSV Export
const exportToCsv = () => {
  try {
    const headers = [
      "Query ID",
      "Query Name",
      "SQL",
      "Type",
      "Tags",
      "Author",
      "Run Date",
      "Component ID",
      "AST Structure",
      "Cluster ID",
      "Description",
      "Mentioned",
      "Inferred",
      "Features",
    ];

    const csvContent = [
      headers.join(","),
      ...filteredSearchResults.value.map((query) => {
        const row = [
          `"${query.id}"`,
          `"${query.queryLabel}"`,
          `"${(query.neuronText || "").replace(/"/g, '""')}"`,
          `"${query.componentType}"`,
          `"${query.tags?.join("; ") || ""}"`,
          `"${query.author}"`,
          `"${formatDate(query.queryStartTime) || "N/A"}"`,
          `"${query.componentId}"`,
          `"${(query.neuronText || "").replace(/"/g, '""')}"`,
          `"${query.clusterId}"`,
          `"${(query.description?.main || "").replace(/"/g, '""')}"`,
          `"${query.mentioned?.join("; ") || ""}"`,
          `"${query.inferences?.join("; ") || ""}"`,
          `"${query.features?.join("; ") || ""}"`,
        ];
        return row.join(",");
      }),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `queries_export_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error exporting CSV:", error);
  }
};

// Date formatting
function formatDate(dateString: string) {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Filter persistence functions
const saveCurrentFilters = () => {
  const filterState: FilterState = {
    searchTerm: searchConditions.value.map(c => c.text).join(" "),
    selectedFeatures: [], // Not used in this view
    selectedTags: [], // Not used in this view
    selectedAuthors: [], // Not used in this view
    selectedQueryTypes: [], // Not used in this view
    selectedTables: [], // Not used in this view
    selectedColumns: [], // Not used in this view
    selectedIndicationEnrichments: [], // Not used in this view
    selectedMedicationEnrichments: [], // Not used in this view
    showUniqueQueries: showUniqueQueries.value,
  };
  saveFiltersToStorage(filterState);
};

const loadPersistedFilters = () => {
  const persisted = loadFiltersFromStorage();
  if (persisted && persisted.searchTerm) {
    // For AdvancedSearch, we'll restore the search term to the first condition
    if (searchConditions.value.length > 0) {
      searchConditions.value[0].text = persisted.searchTerm;
    }
    if (persisted.showUniqueQueries !== undefined) {
      showUniqueQueries.value = persisted.showUniqueQueries;
    }
  }
};

// Navigation
const goToDetails = (queryId: string) => {
  // Navigate to query details in the same tab
  router.push(`/query/${queryId}`);
};

// Set operations
const union = (a: Set<string>, b: Set<string>): Set<string> => {
  const result = new Set<string>();
  a.forEach((x) => result.add(x));
  b.forEach((x) => result.add(x));
  return result;
};

const intersect = (a: Set<string>, b: Set<string>): Set<string> => {
  const result = new Set<string>();
  a.forEach((x) => {
    if (b.has(x)) result.add(x);
  });
  return result;
};

const difference = (a: Set<string>, b: Set<string>): Set<string> => {
  const result = new Set<string>();
  a.forEach((x) => {
    if (!b.has(x)) result.add(x);
  });
  return result;
};

// Match finding helper
const findMatches = (condition: SearchCondition): Set<string> => {
  const set = new Set<string>();
  const textLower = condition.text.toLowerCase().trim();

  // Get selected or default scopes
  const selectedScopes =
    condition.scopes.length > 0
      ? condition.scopes.map((s) => s.value)
      : scopeOptions.map((s) => s.value);

  queries.value.forEach((q) => {
    let matchesAnyScope = false;

    for (const scope of selectedScopes) {
      let fieldValue: any = (q as any)[scope];

      if (scope === "timePeriod") {
        fieldValue = `${q.startDate || ""} - ${q.endDate || ""}`.trim();
      }

      if (fieldValue == null) continue;

      const searchValue = String(fieldValue ?? "").toLowerCase();
      const isArray = Array.isArray(fieldValue);

      let matches = false;
      if (!textLower) {
        matches = isArray ? fieldValue.length > 0 : !!fieldValue;
      } else {
        if (isArray) {
          matches = fieldValue.some((item: any) =>
            String(item).toLowerCase().includes(textLower)
          );
        } else {
          matches = searchValue.includes(textLower);
        }
      }

      if (matches) {
        matchesAnyScope = true;
        break;
      }
    }

    // Special handling for author scope
    if (selectedScopes.includes("author") && !matchesAnyScope) {
      const authorValue = q.author?.toLowerCase() || "";
      matchesAnyScope = !textLower || authorValue.includes(textLower);
    }

    if (matchesAnyScope) {
      set.add(q.id);
    }
  });

  return set;
};

// Add handleToggleUnique function
const handleToggleUnique = (show: boolean) => {
  showUniqueQueries.value = show;
  executeSearch();
};

// Add the getSuggestionIndex function
function getSuggestionIndex(suggestion: any): number {
  return suggestions.value.findIndex(
    (s) =>
      s.text === suggestion.text && s.matchedScope === suggestion.matchedScope
  );
}

// Handle manual search trigger
const handleManualSearch = () => {
  hasSearched.value = true;
  executeSearch();
};

// Handle suggestion selection
const handleSuggestionSelect = (
  suggestion: any,
  condition: SearchCondition
) => {
  condition.text = suggestion.id;
  showSuggestions.value = false;
  suggestions.value = [];
  hasSearched.value = true;
  executeSearch();
};

onMounted(() => {
  // Load persisted filters first
  loadPersistedFilters();
  
  filteredSearchResults.value = queries.value;
  displayedResults.value = queries.value.slice(0, 100);
  isInitialized.value = true;
});

watch(
  [searchConditions, sortState],
  () => {
    // Only execute search if user has actually searched
    if (isInitialized.value && hasSearched.value) {
      executeSearch();
    }
  },
  { deep: true }
);

// Watch for filter changes and save to localStorage
watch([searchConditions, showUniqueQueries], () => {
  saveCurrentFilters();
}, { deep: true });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", { class: "flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 mt-6" }, [
        _cache[1] || (_cache[1] = _createElementVNode("h1", { class: "text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900" }, " Advanced Query Search ", -1)),
        _createElementVNode("button", {
          onClick: exportToCsv,
          class: "w-full sm:w-auto px-4 py-2 bg-[#157813] hover:bg-[#0f5a0d] text-white rounded-lg transition-colors"
        }, " Export CSV ")
      ]),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(searchConditions.value, (condition, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: condition.id,
              class: "space-y-4"
            }, [
              (index > 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    _cache[3] || (_cache[3] = _createElementVNode("div", { class: "flex-1 border-t-2 border-dotted border-gray-200" }, null, -1)),
                    _withDirectives(_createElementVNode("select", {
                      "onUpdate:modelValue": ($event: any) => ((condition.logic) = $event),
                      class: "px-3 py-1 bg-white border-2 border-[#157813] text-[#157813] rounded-md font-medium focus:ring-2 focus:ring-[#157813]/30"
                    }, [...(_cache[2] || (_cache[2] = [
                      _createElementVNode("option", { value: "AND" }, "AND", -1),
                      _createElementVNode("option", { value: "OR" }, "OR", -1),
                      _createElementVNode("option", { value: "NOT" }, "NOT", -1)
                    ]))], 8, _hoisted_6), [
                      [_vModelSelect, condition.logic]
                    ]),
                    _cache[4] || (_cache[4] = _createElementVNode("div", { class: "flex-1 border-t-2 border-dotted border-gray-200" }, null, -1))
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", _hoisted_8, [
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": ($event: any) => ((condition.text) = $event),
                    onInput: ($event: any) => (
                    _unref(debouncedGenerateSuggestions)(condition.text, condition)
                  ),
                    onKeydown: handleKeyDown,
                    placeholder: 
                    condition.scopes.length
                      ? `Search in ${condition.scopes
                          .map((s) => s.label)
                          .join(', ')}...`
                      : 'Search across all fields (e.g., patient, SELECT, diabetes)...'
                  ,
                    class: "w-full h-16 px-6 pr-16 rounded-full border-2 border-[#157813] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#34dc1c] text-lg"
                  }, null, 40, _hoisted_9), [
                    [_vModelText, condition.text]
                  ]),
                  _createElementVNode("button", {
                    onClick: handleManualSearch,
                    class: "absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-[#157813] text-white rounded-full hover:bg-[#0e5a0c] transition-colors"
                  }, [...(_cache[5] || (_cache[5] = [
                    _createElementVNode("svg", {
                      class: "h-6 w-6",
                      fill: "none",
                      viewBox: "0 0 24 24",
                      stroke: "currentColor"
                    }, [
                      _createElementVNode("path", {
                        "stroke-linecap": "round",
                        "stroke-linejoin": "round",
                        "stroke-width": "2",
                        d: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      })
                    ], -1)
                  ]))]),
                  _createVNode(SuggestionDropdown, {
                    suggestions: 
                    suggestions.value.map((s) => ({
                      id: s.id,
                      type: _unref(mapQueryType)(s.type),
                      text: s.text,
                      author: s.author,
                      runCount: s.runCount,
                      date: s.date,
                      tags: s.tags,
                      features: s.features,
                    }))
                  ,
                    "is-visible": showSuggestions.value,
                    "is-loading": isGeneratingSuggestions.value,
                    "highlighted-index": selectedSuggestionIndex.value,
                    "show-footer": true,
                    "search-text": condition.text,
                    onSelect: 
                    (suggestion) =>
                      handleSuggestionSelect(suggestion, condition)
                  ,
                    onHighlight: (index) => (selectedSuggestionIndex.value = index),
                    onClose: _cache[0] || (_cache[0] = 
                    () => {
                      showSuggestions.value = false;
                      suggestions.value = [];
                    }
                  ),
                    onSearch: handleManualSearch,
                    onSearchClick: handleManualSearch
                  }, null, 8, ["suggestions", "is-visible", "is-loading", "highlighted-index", "search-text", "onSelect", "onHighlight"])
                ]),
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("div", _hoisted_11, [
                    _createElementVNode("div", {
                      class: "h-12 border-2 border-gray-200 rounded-lg px-3 flex items-center cursor-pointer hover:border-[#157813] transition-colors",
                      onClick: ($event: any) => (toggleDropdown(condition.id))
                    }, [
                      _createElementVNode("div", _hoisted_13, [
                        (condition.scopes.length)
                          ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(condition.scopes, (scope) => {
                              return (_openBlock(), _createElementBlock("div", {
                                key: scope.value,
                                class: "bg-[#157813]/10 text-[#157813] px-2 py-1 rounded-full text-sm flex items-center gap-1"
                              }, [
                                _createTextVNode(_toDisplayString(scope.label) + " ", 1),
                                _createElementVNode("button", {
                                  onClick: _withModifiers(($event: any) => (
                              condition.scopes = condition.scopes.filter(
                                (s) => s.value !== scope.value
                              )
                            ), ["stop"]),
                                  class: "hover:text-[#0f5a0d]"
                                }, " × ", 8, _hoisted_14)
                              ]))
                            }), 128))
                          : (_openBlock(), _createElementBlock("div", _hoisted_15, [...(_cache[6] || (_cache[6] = [
                              _createElementVNode("svg", {
                                class: "w-3 h-3",
                                fill: "none",
                                stroke: "currentColor",
                                viewBox: "0 0 24 24"
                              }, [
                                _createElementVNode("path", {
                                  "stroke-linecap": "round",
                                  "stroke-linejoin": "round",
                                  "stroke-width": "2",
                                  d: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                })
                              ], -1),
                              _createTextVNode(" All Fields (General Search) ", -1)
                            ]))]))
                      ]),
                      (_openBlock(), _createElementBlock("svg", {
                        class: _normalizeClass(["w-5 h-5 text-[#157813] ml-2 transform transition-transform", { 'rotate-180': dropdownStates.value[condition.id] }]),
                        fill: "none",
                        stroke: "currentColor"
                      }, [...(_cache[7] || (_cache[7] = [
                        _createElementVNode("path", {
                          "stroke-linecap": "round",
                          "stroke-linejoin": "round",
                          "stroke-width": "2",
                          d: "M19 9l-7 7-7-7"
                        }, null, -1)
                      ]))], 2))
                    ], 8, _hoisted_12),
                    _withDirectives(_createElementVNode("div", _hoisted_16, [
                      _createElementVNode("div", _hoisted_17, [
                        _createElementVNode("div", _hoisted_18, [
                          _cache[9] || (_cache[9] = _createElementVNode("h3", { class: "text-sm font-semibold text-[#157813]" }, " Search Fields ", -1)),
                          _createElementVNode("button", {
                            onClick: _withModifiers(($event: any) => (condition.scopes = []), ["stop"]),
                            class: "text-xs text-[#157813] hover:text-[#0f5a0d] flex items-center gap-1"
                          }, [...(_cache[8] || (_cache[8] = [
                            _createTextVNode(" Clear All ", -1),
                            _createElementVNode("svg", {
                              class: "w-3 h-3",
                              fill: "none",
                              stroke: "currentColor"
                            }, [
                              _createElementVNode("path", {
                                "stroke-linecap": "round",
                                "stroke-linejoin": "round",
                                "stroke-width": "2",
                                d: "M6 18L18 6M6 6l12 12"
                              })
                            ], -1)
                          ]))], 8, _hoisted_19)
                        ]),
                        _createElementVNode("div", _hoisted_20, [
                          _withDirectives(_createElementVNode("input", {
                            type: "text",
                            placeholder: "Search fields...",
                            class: "w-full pl-8 pr-3 py-2 border-b border-gray-200 focus:outline-none focus:border-[#157813] text-sm",
                            "onUpdate:modelValue": ($event: any) => ((searchQueries.value[condition.id]) = $event)
                          }, null, 8, _hoisted_21), [
                            [_vModelText, searchQueries.value[condition.id]]
                          ]),
                          _cache[10] || (_cache[10] = _createElementVNode("svg", {
                            class: "w-4 h-4 absolute left-2 top-2.5 text-gray-400",
                            fill: "none",
                            stroke: "currentColor"
                          }, [
                            _createElementVNode("path", {
                              "stroke-linecap": "round",
                              "stroke-linejoin": "round",
                              "stroke-width": "2",
                              d: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            })
                          ], -1))
                        ]),
                        _createElementVNode("div", _hoisted_22, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredOptions(condition), (option) => {
                            return (_openBlock(), _createElementBlock("div", {
                              key: option.value,
                              class: "flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer",
                              onClick: _withModifiers(($event: any) => (toggleOption(condition, option)), ["stop"])
                            }, [
                              _createElementVNode("div", {
                                class: _normalizeClass(["w-5 h-5 flex items-center justify-center mr-2 border-2 border-[#157813]/30 rounded-sm transition-colors", {
                              'bg-[#157813] border-[#157813]': isSelected(
                                condition,
                                option
                              ),
                            }])
                              }, [
                                (isSelected(condition, option))
                                  ? (_openBlock(), _createElementBlock("svg", _hoisted_24, [...(_cache[11] || (_cache[11] = [
                                      _createElementVNode("path", {
                                        "stroke-linecap": "round",
                                        "stroke-linejoin": "round",
                                        "stroke-width": "2",
                                        d: "M5 13l4 4L19 7"
                                      }, null, -1)
                                    ]))]))
                                  : _createCommentVNode("", true)
                              ], 2),
                              _createElementVNode("span", _hoisted_25, _toDisplayString(option.label), 1)
                            ], 8, _hoisted_23))
                          }), 128))
                        ])
                      ])
                    ], 512), [
                      [_vShow, dropdownStates.value[condition.id]]
                    ])
                  ])
                ]),
                (index > 0)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      onClick: ($event: any) => (removeCondition(index)),
                      class: "sm:self-center px-3 py-2 text-gray-400 hover:text-red-600"
                    }, [...(_cache[12] || (_cache[12] = [
                      _createElementVNode("svg", {
                        class: "w-5 h-5",
                        fill: "none",
                        stroke: "currentColor"
                      }, [
                        _createElementVNode("path", {
                          "stroke-linecap": "round",
                          "stroke-linejoin": "round",
                          "stroke-width": "2",
                          d: "M6 18L18 6M6 6l12 12"
                        })
                      ], -1)
                    ]))], 8, _hoisted_26))
                  : _createCommentVNode("", true)
              ])
            ]))
          }), 128)),
          _createElementVNode("div", { class: "flex flex-wrap gap-3 mt-6" }, [
            _createElementVNode("button", {
              onClick: addCondition,
              class: "w-full sm:w-auto px-5 py-2.5 bg-white border-2 border-[#157813] text-[#157813] rounded-lg hover:bg-[#157813]/5"
            }, " + Add Condition "),
            _createElementVNode("button", {
              onClick: handleManualSearch,
              class: "w-full sm:w-auto px-6 py-2.5 bg-[#157813] hover:bg-[#0f5a0d] text-white rounded-lg shadow-sm"
            }, " Search Queries ")
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_27, [
        _createElementVNode("div", _hoisted_28, [
          (
              searchConditions.value[0] && searchConditions.value[0].scopes.length === 0
            )
            ? (_openBlock(), _createElementBlock("span", _hoisted_29, [...(_cache[13] || (_cache[13] = [
                _createElementVNode("svg", {
                  class: "w-4 h-4 mr-2",
                  fill: "none",
                  stroke: "currentColor",
                  viewBox: "0 0 24 24"
                }, [
                  _createElementVNode("path", {
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round",
                    "stroke-width": "2",
                    d: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  })
                ], -1),
                _createTextVNode(" Global Search ", -1)
              ]))]))
            : (_openBlock(), _createElementBlock("span", _hoisted_30, [...(_cache[14] || (_cache[14] = [
                _createElementVNode("svg", {
                  class: "w-4 h-4 mr-2",
                  fill: "none",
                  stroke: "currentColor",
                  viewBox: "0 0 24 24"
                }, [
                  _createElementVNode("path", {
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round",
                    "stroke-width": "2",
                    d: "M5 13l4 4L19 7"
                  })
                ], -1),
                _createTextVNode(" Field Search ", -1)
              ]))]))
        ]),
        (isSearching.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_31, [
              _createVNode(LoadingIndicator, {
                title: "Searching queries...",
                subtitle: "Processing your search conditions",
                status: "Analyzing database"
              })
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_32, [
              _createElementVNode("div", _hoisted_33, [
                (hasSearched.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_34, [
                      (filteredSearchResults.value.length === 0)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_35, " No results found. Try adjusting your search criteria. "))
                        : (_openBlock(), _createElementBlock("span", _hoisted_36, " Showing " + _toDisplayString(displayedResults.value.length) + " of " + _toDisplayString(filteredSearchResults.value.length) + " results ", 1))
                    ]))
                  : _createCommentVNode("", true)
              ])
            ])),
        (!isSearching.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_37, [
              _createVNode(QueryTable, {
                queries: displayedResults.value,
                headers: tableHeaders,
                "sort-state": sortState.value,
                onSort: sortBy,
                onRowClick: goToDetails,
                onToggleUnique: handleToggleUnique
              }, null, 8, ["queries", "sort-state"])
            ]))
          : _createCommentVNode("", true),
        (filteredSearchResults.value.length > itemsPerPage)
          ? (_openBlock(), _createElementBlock("div", _hoisted_38, [
              _createVNode(Pagination, {
                "current-page": currentPage.value,
                "total-items": filteredSearchResults.value.length,
                "items-per-page": itemsPerPage,
                onPageChange: handlePageChange
              }, null, 8, ["current-page", "total-items"])
            ]))
          : _createCommentVNode("", true),
        (
            hasSearched.value && filteredSearchResults.value.length === 0 && !isSearching.value
          )
          ? (_openBlock(), _createElementBlock("div", _hoisted_39, [...(_cache[15] || (_cache[15] = [
              _createStaticVNode("<div class=\"text-gray-400 mb-4\" data-v-f83ad2b0><svg class=\"w-16 h-16 mx-auto\" fill=\"none\" stroke=\"currentColor\" viewBox=\"0 0 24 24\" data-v-f83ad2b0><path stroke-linecap=\"round\" stroke-linejoin=\"round\" stroke-width=\"2\" d=\"M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z\" data-v-f83ad2b0></path></svg></div><h3 class=\"text-lg font-medium text-gray-900 mb-2\" data-v-f83ad2b0> No Results Found </h3><p class=\"text-gray-600 max-w-md mx-auto px-4\" data-v-f83ad2b0> Try adjusting your search criteria or removing some filters to see more results. </p>", 3)
            ]))]))
          : _createCommentVNode("", true)
      ])
    ])
  ]))
}
}

})